// payment/receipt.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useFormik, FormikProps } from 'formik';
import { PaymentFormState, PaymentProps, TformMode } from '../types';
import useControlledNavigation from '../../../hooks/useControlledNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { display } from '../../../redux/reducers/sidebar';
import { useLocation, useParams } from 'react-router-dom';
import { useCreatePdfMutation } from '../../../redux/apis/createPdf.api';
import API_ENDPOINTS from '../../../apiEndpoints';
import { settingsClose } from '../../../redux/reducers/companySkipSave';
import { createPaymentValidationSchema } from '../utils/validationSchema';
import { useGetLastUsedBankOrCashAccountQuery } from '../api/payment.api';
import {
  CreateTransactionWithSpecificTimev2MutationVariables,
  TransactionDetailsSpecificTransactionInput,
  TransactionMasterSpecificTransactionInput,
  TransactionTDSDetailsSpecificTransactionInput,
  TransactionType,
} from '../../../graphql';
import { createTransactionWithSpecificTime } from '../api/transaction.api';
import { useListCompanyPreferenceQuery } from '../../../redux/apis/companyPreference.api';
import { useListGeneralSettingQuery } from '../../../redux/apis/VoucherSettings.api';

type OUTR = {
  location: ReturnType<typeof useLocation>;
  initialState: PaymentFormState;
  LastAccountsLoading: Boolean;
  formik: FormikProps<PaymentFormState>;
};

export const usePayment = ({ onSaveComplete, method, paidFrom, paidTo, type }: PaymentProps & { type: 'payment' | 'receipt' }): OUTR => {
  console.log('usePayment called with props:', { onSaveComplete, method, paidFrom, paidTo, type });
  
  const dispatch = useDispatch();
  const navigate = useControlledNavigation();
  dispatch(display());
  const location = useLocation();

  // Parse URL parameters for pre-filling form
  const urlParams = new URLSearchParams(location.search);
  const urlPaidTo = urlParams.get('paidto');
  const urlPayableAmount = urlParams.get('payableamount');
  const urlMethod = urlParams.get('method');
  
  // Validate URL method parameter - only allow CashPayment or BankPayment
  const validMethods = ['CashPayment', 'BankPayment'];
  const validatedUrlMethod = urlMethod && validMethods.includes(urlMethod) ? urlMethod : null;
  
  console.log('URL method validation:', { urlMethod, validatedUrlMethod, validMethods });

  if (!onSaveComplete) {
    onSaveComplete = () => navigate('/paymententries');
  }

  const [createPdf, progress] = useCreatePdfMutation();

  const companyDetail = useSelector((state: any) => state?.companyReducer?.companyDetails);
  let userPreferece = useSelector((state: any) => state?.userPrefrenceandReducer);
  let data = { vouchernumber: '12345' };

  let path = `${userPreferece?.financialYear?.companyid}/${userPreferece?.financialYear?.companybranchid}/${userPreferece?.financialYear?.financialyearid}/`;
  let url = path + `Invoices/Payments/Ledgerx_Payment_${data.vouchernumber}.pdf`;
  let uri = `${API_ENDPOINTS.REACT_APP_S3_BUCKET_URL}/${url}`;
  const { mode: initMode, id } = useParams<{ mode?: TformMode; id?: string }>();
  const [mode, setMode] = useState<TformMode>(initMode ?? 'VIEW');

  const [balanceAmount, setBalanceAmount] = useState({
    fromAccount: 0,
    toAccount: 0,
  });

  const { data: companyPreference, isLoading, isFetching } = useListCompanyPreferenceQuery();

  const [initialState, setInitialState] = useState<PaymentFormState>({
    vouchernumber: '',
    date: 0,
    method: validatedUrlMethod || method || 'CashPayment',
    paidfrom: paidFrom ?? (method === 'CashPayment' ? 3 : 39),
    paidto: urlPaidTo ? parseInt(urlPaidTo) : paidTo ?? 0,
    payableamount: urlPayableAmount || '',
    tdsmanual: 0,
    tds: false,
    tdsapplicable: '',
    tdsamount: '',
    paidamount: '',
    bankrefno: '',
    remarks: '',
    attachment: '',
    seriesvouchers: '',
    tdspercentage: '',
    towards: '',
    tdssection: '',
    isParty: false,
    manualvoucher: false,
    bankRefNo: '',
  });

  console.log('Initial state set to:', initialState);

  useEffect(() => {
    console.log({isFetching,isLoading,companyPreference,userPreferece});
    if (!isLoading && !isFetching&& companyPreference) {
      const postingDatePref = companyPreference?.find(
        (p) =>
          p.attributetypecd === 'MyPostingDate' &&
          String(p?.additionalvalue ?? '') === String(userPreferece?.financialYear?.financialyearid ?? '')
      );
      let savedPostingDate = Number(postingDatePref?.attributevalue ? postingDatePref?.attributevalue : 0);
      console.log(savedPostingDate,postingDatePref)
      setInitialState((prevState) => ({
        ...prevState,
        date: savedPostingDate || 0,
      }));
    }
  }, [isLoading, isFetching,companyPreference,userPreferece]);

  const { data: LastAccounts, isLoading: LastAccountsLoading } = useGetLastUsedBankOrCashAccountQuery({
    transactiontypecd: TransactionType[type],
  });
  console.log({ LastAccounts });

  const { saveSkipStatus } = useSelector((state: any) => state?.companySipSaveReducer);
  useEffect(() => {
    if (saveSkipStatus == false) {
      dispatch(settingsClose());
      navigate('/companysetup');
    }
  }, [dispatch, navigate, saveSkipStatus]);

  const {
    data: generalSettings,
    isLoading: generalSettingsLoading,
    isFetching: generalSettingsFetching,
  } = useListGeneralSettingQuery(
    {
      entrytypecd: 'Payment',
      financialyearid: userPreferece?.financialYear?.financialyearid,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (!generalSettingsLoading && !generalSettingsFetching && generalSettings?.listGeneralSetting) {
      console.log('Setting voucher number from general settings:', generalSettings.listGeneralSetting);
      
      // Only set voucher number if we don't already have one (for edit mode)
      const currentVoucherNumber = initialState.vouchernumber;
      if (!currentVoucherNumber || currentVoucherNumber === '') {
        generalSettings.listGeneralSetting.forEach((setting) => {
          if (setting?.serialisactive) {
            let vouchernumber = setting?.prefix + String(setting?.nextinvoicenumber) + (setting?.suffix || '');
            console.log('Generated voucher number:', vouchernumber);
            setInitialState((prevState) => ({
              ...prevState,
              vouchernumber: String(vouchernumber) || '',
              seriesvouchers:String(setting?.serialnumber ||'')
            }));
          }
        });
      } else {
        console.log('Keeping existing voucher number:', currentVoucherNumber);
      }
    }
  }, [generalSettings, generalSettingsLoading, generalSettingsFetching, initialState.vouchernumber]);

  console.log({ generalSettings, initialState });
  const validationSchema = useMemo(() => createPaymentValidationSchema(type), [type]);

  const saveChange = async (FormValues: PaymentFormState) => {
    try {
      const transactiontdsdetails: TransactionTDSDetailsSpecificTransactionInput[] = [];
      const transactiondetails: TransactionDetailsSpecificTransactionInput[] = [];

      const paidDetails: TransactionDetailsSpecificTransactionInput = {
        accountid: Number(FormValues.paidto) || 0,
        comments: FormValues.remarks || '',
        debitamount: 0,
        creditamount: Number(FormValues.payableamount) || 0,
        transactiontypecd: 'Debit',
        cancelflag: 0,
        enteredtimestamp: 0,
      };
      const receivedDetails: TransactionDetailsSpecificTransactionInput = {
        accountid: Number(FormValues.paidfrom),
        comments: FormValues.remarks || '',
        debitamount: Number(Number(Number(FormValues.payableamount) - Number(FormValues.tdsamount)).toFixed(2)),
        creditamount: 0,
        transactiontypecd: 'Credit',
        cancelflag: 0,
        enteredtimestamp: 0,
      };
      transactiondetails.push(receivedDetails);
      transactiondetails.push(paidDetails);

      if (FormValues.tdssection) {
        const tdsDetails: TransactionTDSDetailsSpecificTransactionInput = {
          tdsamount: Number(FormValues.tdsamount) || 0,
          tdspercentage: Number(FormValues.tdspercentage) || 0,
          tdsmanual: Number(FormValues.tdsmanual) || 0,
          tdsapplicablecd: FormValues.tdsapplicable || '',
        };
        const tdsTransactionDetails: TransactionDetailsSpecificTransactionInput = {
          accountid: 21,
          comments: FormValues.remarks || '',
          debitamount: FormValues.tdsamount ? Number(FormValues.tdsamount) : 0,
          creditamount: 0,
          transactiontypecd: 'Credit',
          cancelflag: 0,
          enteredtimestamp: 0,
        };
        transactiondetails.push(tdsTransactionDetails);
        transactiontdsdetails.push(tdsDetails);
      }
      const transactionmaster: TransactionMasterSpecificTransactionInput = {
        bankreferencenumber: FormValues.bankrefno || '',
        comments: FormValues.remarks || '',
        entrydate: Number(FormValues.date) || 0,
        pdflink: uri || '',
        serialnumber: FormValues.seriesvouchers || '',
        transactiontypecd: FormValues.method,
        towards: FormValues.towards || '',
        vouchercode: FormValues.vouchernumber,
        cancelflag: 0,
        cancelremark: '',
      };
      const createTransactionWithSpecificTimeInput: CreateTransactionWithSpecificTimev2MutationVariables = {
        createTransactionWithSpecificTimeInput: {
          mode: 1,
          transactionmaster: [transactionmaster],
          transactiondetails,
          transactiontdsdetails,
          transactionrelation: [],
          transactiontcsdetails: [],
          transactionattachment: [],
        },
      };

      let response = await createTransactionWithSpecificTime(createTransactionWithSpecificTimeInput);
      console.log({ response });
      if (response?.createTransactionWithSpecificTimev2?.statuscode === 'Success') {
        let transactionid = response?.createTransactionWithSpecificTimev2?.transactionid;
        createPdf({
          path: url,
          type: 'payment',
          transactionid: transactionid,
        });
        onSaveComplete();
      }
      console.log({ response });
    } catch (error) {
      console.log(error);
    }
  };

  const formik = useFormik({
    initialValues: initialState,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log('=== FORM SUBMISSION TEST ===', formik.dirty);
      console.log('All form values:', values);
      console.log('Towards field:', values.towards);
      console.log('TDS fields:', {
        tdssection: values.tdssection,
        tdspercentage: values.tdspercentage,
        tdsamount: values.tdsamount,
      });
      console.log('Payable amount:', values.payableamount);
      console.log('Paid amount:', values.paidamount);
      console.log('Method:', values.method);
      console.log('Date:', values.date);
      console.log('===========================');
      saveChange(values);
    },
  });

  // Force form re-initialization when initialState changes
  useEffect(() => {
    if (formik) {
      console.log('Re-initializing form with:', initialState);
      formik.setValues(initialState);
    }
  }, [initialState]);

  return {
    location,
    initialState,
    formik,
    LastAccountsLoading,
  };
};
